import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Alert,
  DialogActions,
  Drawer,
} from "@mui/material";

import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import CloseIcon from "@mui/icons-material/Close";

import { useDispatch } from "react-redux";

import NCG_logo from "../../assets/img/ncg_logo.png";

import {
  getNCGDetails,
  // getNCGThumnailDetails,
  getArticleCommentsBulk,
  getArticleLikesBulk,
} from "./VTB.API";

import {
  browseNCGSearchResult,
  updateNCGBrowserPage,
  updateNCGBrowserRow,
  updateNCGLoadingState,
} from "../../store/slice/ncgDetailsSlice";

import { useAppSelector } from "../../store/hooks";

import VTBFilters from "./VTBFilters";
import VTBVideoPlayer from "./VTBVideoPlayer";
import VTBUpNext from "./VTBUpNext";

import { goToTop } from "../../utils/helper";
import Pagination from "../pagination";
import VTBCommentsSection from "./VTBCommentsSection";
import VTBLikeButton from "./VTBLikeButton";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";

import FilterListIcon from "@mui/icons-material/FilterList";
import SortIcon from "@mui/icons-material/Sort";
import { Clear, FilterAlt } from "@mui/icons-material";

// CONSTANTS
const VTB_API_BASE =
  "https://25hutgazcd.execute-api.ap-south-1.amazonaws.com/prod";

const VTB_API_KEY = "Vrbz5r0GLr2xG8hP4eBIk6PPmxn59loX2s7zDMiw";
const HLS_VIDEO_ID = "VTB Library_I_Videos_Case 345";

// TYPES
interface VideoRecord {
  thumbnailUrl?: string;
  videoId?: string;
  title?: string;
  module?: string;
  MeSHWords?: string;
  keywords?: string;
  accessLevel?: string;
  uploadDate?: string;
  video_id?: string;

  article_id?: string | number;
  category?: string;
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function VTBFinderModule() {
  const dispatch = useDispatch();

  const [currentView, setCurrentView] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState("sort");
  const [selectedVideo, setSelectedVideo] = useState<VideoRecord | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imgThumbnail, setImgThumbnail] = useState<any | null>(null);
  const [likeCounts, setLikeCounts] = useState<Record<string, number>>({});
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>(
    {},
  );

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isSortDialogOpen, setIsSortDialogOpen] = useState(false);

  const commentsSectionRef = useRef<HTMLDivElement | null>(null);
  const [focusCommentInput, setFocusCommentInput] = useState(false);

  // ==========================================================
  // REDUX DATA
  // ==========================================================

  const vTBDetails = useAppSelector(
    (state: any) => state.ncgDetails?.result?.docs,
  );

  const vTBDetailsHits = useAppSelector(
    (state: any) => state.ncgDetails?.result?.hits,
  );

  const rows = useAppSelector((state: any) => state.ncgDetails?.rows);
  const currentPage = useAppSelector((state: any) => state.ncgDetails?.page);
  const vTBFilterDetails = useAppSelector((state: any) => state.ncgDetails);

  // FILTER SELECTORS

  const vTBYearFilterDetails = useAppSelector(
    (state: any) => state.ncgDetails.yearfrom || [],
  );

  const vTBPublisherFilterDetails = useAppSelector(
    (state: any) => state.ncgDetails.publisher_name || [],
  );

  const vTBGenderFilterDetails = useAppSelector(
    (state: any) => state.ncgDetails.gender || [],
  );

  const vTBAgeGroupFilterDetails = useAppSelector(
    (state: any) => state.ncgDetails.age_group || [],
  );

  const vTBDiagnosisFilterDetails = useAppSelector(
    (state: any) => state.ncgDetails.diagnosis || [],
  );

  const vTBPresentingCentreFilterDetails = useAppSelector(
    (state: any) => state.ncgDetails.presenting_centre || [],
  );

  const vTBPrimarySiteFilterDetails = useAppSelector(
    (state: any) => state.ncgDetails.primary_site || [],
  );

  const vTBCategoryFilterDetails = useAppSelector(
    (state: any) => state.ncgDetails.category || [],
  );

  const vTBDMGFilterDetails = useAppSelector(
    (state: any) => state.ncgDetails.dmg || [],
  );

  const vTBStageOfCancerFilterDetails = useAppSelector(
    (state: any) => state.ncgDetails.stage_of_cancer || [],
  );

  // ==========================================================
  // PAGINATION COUNT
  // ==========================================================

  const totalHits = Number(vTBDetailsHits || 0);

  const rowsPerPage = Number(rows || 10);

  const count = rowsPerPage > 0 ? Math.ceil(totalHits / rowsPerPage) : 0;

  // ==========================================================
  // ARTICLEIDS
  // ==========================================================
  const articleIds = useMemo(() => {
    if (!Array.isArray(vTBDetails)) {
      return [];
    }

    return vTBDetails
      .map((video: any) => video?.article_id)
      .filter(
        (articleId: any) =>
          articleId !== undefined && articleId !== null && articleId !== "",
      );
  }, [vTBDetails]);

  // ==========================================================
  // USEEFFECTS
  // ==========================================================
  // FETCH VTB LIBRARY
  useEffect(() => {
    let isMounted = true;

    const fetchVTBLibraryDetails = async () => {
      try {
        setError(null);
        const isSearchRequest = appliedSearchQuery.trim().length > 0;

        if (isSearchRequest) {
          setSearchLoading(true);
        }

        if (!isSearchRequest) {
          setInitialLoading(true);
        }

        const apiPayload: any = {
          page: currentPage,
          rows: rows,
          sort: "title asc",

          json_facet: JSON.stringify({
            yearfrom: {
              type: "terms",
              field: "yearfrom",
              limit: 100,
              sort: "index desc",
            },

            publisher_name: {
              type: "terms",
              field: "publisher_name",
              limit: 100,
            },

            gender: {
              type: "terms",
              field: "gender",
              limit: 100,
            },

            age_group: {
              type: "terms",
              field: "age_group",
              limit: 100,
            },

            diagnosis: {
              type: "terms",
              field: "diagnosis",
              limit: 100,
            },

            presenting_centre: {
              type: "terms",
              field: "presenting_centre",
              limit: 100,
            },

            primary_site: {
              type: "terms",
              field: "primary_site",
              limit: 100,
            },

            category: {
              type: "terms",
              field: "category",
              limit: 100,
            },

            dmg: {
              type: "terms",
              field: "dmg",
              limit: 100,
            },

            stage_of_cancer: {
              type: "terms",
              field: "stage_of_cancer",
              limit: 100,
            },
          }),
        };

        // SEARCH BY TITLE
        if (appliedSearchQuery.trim().length > 0) {
          apiPayload.title_fz = `(${appliedSearchQuery.trim()})`;
        }

        // FILTER HELPER
        const createFilterValue = (values: any[]) => {
          return `(${values.map((value: any) => `"${value}"`).join(" OR ")})`;
        };

        // YEAR FILTER
        if (
          Array.isArray(vTBFilterDetails?.yearfrom) &&
          vTBFilterDetails.yearfrom.length > 0
        ) {
          apiPayload.fq__yearfrom = createFilterValue(
            vTBFilterDetails.yearfrom,
          );
        }

        // PUBLISHER
        if (
          Array.isArray(vTBFilterDetails?.publisher_name) &&
          vTBFilterDetails.publisher_name.length > 0
        ) {
          apiPayload.fq__publisher_name = createFilterValue(
            vTBFilterDetails.publisher_name,
          );
        }

        // GENDER
        if (
          Array.isArray(vTBFilterDetails?.gender) &&
          vTBFilterDetails.gender.length > 0
        ) {
          apiPayload.fq__gender = createFilterValue(vTBFilterDetails.gender);
        }

        // AGE GROUP
        if (
          Array.isArray(vTBFilterDetails?.age_group) &&
          vTBFilterDetails.age_group.length > 0
        ) {
          apiPayload.fq__age_group = createFilterValue(
            vTBFilterDetails.age_group,
          );
        }

        // DIAGNOSIS
        if (
          Array.isArray(vTBFilterDetails?.diagnosis) &&
          vTBFilterDetails.diagnosis.length > 0
        ) {
          apiPayload.fq__diagnosis = createFilterValue(
            vTBFilterDetails.diagnosis,
          );
        }

        // PRESENTING CENTRE
        if (
          Array.isArray(vTBFilterDetails?.presenting_centre) &&
          vTBFilterDetails.presenting_centre.length > 0
        ) {
          apiPayload.fq__presenting_centre = createFilterValue(
            vTBFilterDetails.presenting_centre,
          );
        }

        // PRIMARY SITE
        if (
          Array.isArray(vTBFilterDetails?.primary_site) &&
          vTBFilterDetails.primary_site.length > 0
        ) {
          apiPayload.fq__primary_site = createFilterValue(
            vTBFilterDetails.primary_site,
          );
        }

        // CATEGORY
        if (
          Array.isArray(vTBFilterDetails?.category) &&
          vTBFilterDetails.category.length > 0
        ) {
          apiPayload.fq__category = createFilterValue(
            vTBFilterDetails.category,
          );
        }

        // DMG
        if (
          Array.isArray(vTBFilterDetails?.dmg) &&
          vTBFilterDetails.dmg.length > 0
        ) {
          apiPayload.fq__dmg = createFilterValue(vTBFilterDetails.dmg);
        }

        // STAGE OF CANCER
        if (
          Array.isArray(vTBFilterDetails?.stage_of_cancer) &&
          vTBFilterDetails.stage_of_cancer.length > 0
        ) {
          apiPayload.fq__stage_of_cancer = createFilterValue(
            vTBFilterDetails.stage_of_cancer,
          );
        }

        const response = await getNCGDetails(apiPayload);

        if (!isMounted) {
          return;
        }

        dispatch(browseNCGSearchResult(response?.data?.data));
        // // THUMBNAIL API
        // const thumbnailResponse = await getNCGThumnailDetails();

        // if (!isMounted) {
        //   return;
        // }
        // setImgThumbnail(thumbnailResponse?.data?.data || null);

        // try {
        //   const thumbnailResponse = await getNCGThumnailDetails();

        //   if (!isMounted) {
        //     return;
        //   }

        //   const thumbnailData = thumbnailResponse?.data?.data;

        //   // API returned valid thumbnail
        //   if (thumbnailData?.thumbnailUrl) {
        //     setImgThumbnail(thumbnailData);
        //   } else {
        //     // API returned null / Video ID not found
        //     setImgThumbnail(null);
        //   }
        // } catch (thumbnailError) {
        //   console.error("Failed to fetch thumbnail:", thumbnailError);

        //   // Don't show library error
        //   // Just remove thumbnail
        //   if (isMounted) {
        //     setImgThumbnail(null);
        //   }
        // }
      } catch (error) {
        console.error("Failed to fetch NCG data:", error);

        if (isMounted) {
          setError("Failed to load NCG video library.");
        }
      } finally {
        if (isMounted) {
          setSearchLoading(false);
          setInitialLoading(false);
          dispatch(updateNCGLoadingState(false));
        }
      }
    };

    fetchVTBLibraryDetails();

    return () => {
      isMounted = false;
    };
  }, [
    rows,
    currentPage,
    appliedSearchQuery,
    vTBYearFilterDetails,
    vTBPublisherFilterDetails,
    vTBGenderFilterDetails,
    vTBAgeGroupFilterDetails,
    vTBDiagnosisFilterDetails,
    vTBPresentingCentreFilterDetails,
    vTBPrimarySiteFilterDetails,
    vTBCategoryFilterDetails,
    vTBDMGFilterDetails,
    vTBStageOfCancerFilterDetails,

    dispatch,
  ]);

  // FETCH LIKES & COMMENTS
  useEffect(() => {
    const fetchLikesAndComments = async () => {
      if (articleIds.length === 0) {
        return;
      }

      try {
        // Call both APIs at the same time
        const [likesResponse, commentsResponse] = await Promise.all([
          getArticleLikesBulk(articleIds),
          getArticleCommentsBulk(articleIds),
        ]);

        const likesMap: Record<string, number> = {};
        const likesData = likesResponse?.data?.data || [];

        likesData.forEach((item: any) => {
          likesMap[String(item.article_id)] = Number(item.liked_count || 0);
        });

        const commentsMap: Record<string, number> = {};
        const commentsData = commentsResponse?.data?.data || [];

        commentsData.forEach((item: any) => {
          commentsMap[String(item.article_id)] = Number(
            item.comment_count || 0,
          );
        });

        setLikeCounts(likesMap);
        setCommentCounts(commentsMap);
      } catch (error) {
        console.error("Failed to fetch likes/comments:", error);
      }
    };

    fetchLikesAndComments();
  }, [articleIds, currentView]);

  // ==========================================================
  // FUNCTIONS
  // ==========================================================

  // SEARCH HANDLER
  const handleSearch = () => {
    const trimmedValue = searchQuery.trim();

    setAppliedSearchQuery(trimmedValue);
  };

  // CLEAR SEARCH
  const handleClearSearch = () => {
    setSearchQuery("");
    setAppliedSearchQuery("");
  };

  // ENTER KEY SEARCH
  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  // PAGE CHANGE
  const handlePageChange = (page: number) => {
    dispatch(updateNCGBrowserPage(page));

    goToTop();
  };

  // ROW CHANGE
  const getRowsPerPage = (value: number) => {
    dispatch(updateNCGBrowserPage(1));
    dispatch(updateNCGBrowserRow(value));
    goToTop();
  };

  // CARD CLICK
  const handleCardClick = (video: VideoRecord) => {
    setSelectedVideo(video);
    setIsModalOpen(true);
  };

  // CLOSE MODAL
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // WATCH VIDEO
  const handleWatchVideo = () => {
    if (!selectedVideo) {
      return;
    }

    setIsModalOpen(false);
    setCurrentView(1);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // SIDEBAR VIDEO CLICK
  const handleSidebarClick = (video: VideoRecord) => {
    setSelectedVideo(video);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleOpenFilters = () => {
    setIsFilterDrawerOpen(true);
  };

  const handleCloseFilters = () => {
    setIsFilterDrawerOpen(false);
  };

  const handleOpenSort = () => {
    setIsSortDialogOpen(true);
  };

  const handleCloseSort = () => {
    setIsSortDialogOpen(false);
  };

  const handleCommentClick = () => {
    commentsSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });

    setFocusCommentInput(false);

    setTimeout(() => {
      setFocusCommentInput(true);
    }, 100);
  };

  // ==========================================================
  // LIBRARY VIEW
  // ==========================================================

  //   const LibraryView = () => {
  //     // --------------------------------------------------------
  //     // INITIAL PAGE LOADING ONLY
  //     //
  //     // IMPORTANT:
  //     // Search loading does NOT come here.
  //     // Therefore existing cards remain visible while searching.
  //     // --------------------------------------------------------

  //     if (initialLoading && (!vTBDetails || vTBDetails.length === 0)) {
  //       return (
  //         <Box
  //           sx={{
  //             display: "flex",
  //             justifyContent: "center",
  //             py: 10,
  //           }}
  //         >
  //           <CircularProgress />
  //         </Box>
  //       );
  //     }

  //     if (error) {
  //       return (
  //         <Alert severity="error" sx={{ mt: 2 }}>
  //           {error}
  //         </Alert>
  //       );
  //     }

  //     return (
  //       <Grid container spacing={4}>
  //         {/* FILTERS */}
  //         <Grid item xs={12} md={3}>
  //           <VTBFilters />
  //         </Grid>

  //         {/* MAIN CONTENT */}
  //         <Grid item xs={12} md={9}>
  //           {/* SEARCH + SORT */}

  //           <Box
  //             sx={{
  //               display: "flex",
  //               gap: 2,
  //               mb: 3,
  //               alignItems: "center",
  //               flexWrap: {
  //                 xs: "wrap",
  //                 md: "nowrap",
  //               },
  //             }}
  //           >
  //             {/* SEARCH */}

  //             <Box
  //               sx={{
  //                 display: "flex",
  //                 alignItems: "stretch",
  //                 flex: 1,
  //                 minWidth: 0,

  //                 width: {
  //                   xs: "100%",
  //                   md: "auto",
  //                 },
  //               }}
  //             >
  //               <TextField
  //                 fullWidth
  //                 size="small"
  //                 placeholder="Search by title"
  //                 value={searchQuery}
  //                 onChange={(event) => setSearchQuery(event.target.value)}
  //                 onKeyDown={handleSearchKeyDown}
  //                 InputProps={{
  //                   endAdornment: searchQuery ? (
  //                     <IconButton
  //                       size="small"
  //                       onClick={handleClearSearch}
  //                       edge="end"
  //                       aria-label="clear search"
  //                     >
  //                       <CloseIcon fontSize="small" />
  //                     </IconButton>
  //                   ) : null,
  //                 }}
  //                 sx={{
  //                   "& .MuiOutlinedInput-root": {
  //                     height: 40,
  //                     backgroundColor: "#fff",

  //                     borderTopRightRadius: 0,
  //                     borderBottomRightRadius: 0,

  //                     "& fieldset": {
  //                       borderColor: "#d0d5dd",
  //                     },

  //                     "&:hover fieldset": {
  //                       borderColor: "#0f4c81",
  //                     },

  //                     "&.Mui-focused fieldset": {
  //                       borderColor: "#0f4c81",
  //                     },
  //                   },

  //                   "& .MuiInputBase-input": {
  //                     fontSize: "14px",
  //                   },
  //                 }}
  //               />

  //               {/* SEARCH BUTTON */}

  //               <Button
  //                 variant="contained"
  //                 onClick={handleSearch}
  //                 disabled={searchLoading}
  //                 sx={{
  //                   height: 40,
  //                   minWidth: 100,

  //                   borderTopLeftRadius: 0,
  //                   borderBottomLeftRadius: 0,

  //                   textTransform: "none",
  //                   fontWeight: 600,
  //                   whiteSpace: "nowrap",

  //                   bgcolor: "#0f4c81",

  //                   "&:hover": {
  //                     bgcolor: "#08365c",
  //                   },

  //                   "&.Mui-disabled": {
  //                     bgcolor: "#0f4c81",
  //                     color: "#fff",
  //                     opacity: 0.8,
  //                   },
  //                 }}
  //               >
  //                 {searchLoading ? (
  //                   <>
  //                     <CircularProgress
  //                       size={18}
  //                       color="inherit"
  //                       sx={{ mr: 1 }}
  //                     />
  //                     Searching...
  //                   </>
  //                 ) : (
  //                   "Search"
  //                 )}
  //               </Button>
  //             </Box>

  //             {/* SORT */}

  //             <TextField
  //               select
  //               size="small"
  //               value={sortOrder}
  //               disabled
  //               onChange={(event) => setSortOrder(event.target.value)}
  //               sx={{
  //                 width: {
  //                   xs: "100%",
  //                   sm: 250,
  //                   md: 250,
  //                 },

  //                 "& .MuiOutlinedInput-root": {
  //                   height: 40,
  //                   backgroundColor: "#fff",
  //                 },

  //                 "& .MuiInputBase-input": {
  //                   fontSize: "14px",
  //                 },
  //               }}
  //             >
  //               <MenuItem value="sort">SORT</MenuItem>
  //               <MenuItem value="latest">Presenting Year (Latest First)</MenuItem>
  //               <MenuItem value="earliest">
  //                 Presenting Year (Earliest First)
  //               </MenuItem>
  //             </TextField>
  //           </Box>

  //           {/* ================================================= */}
  //           {/* VIDEO CARDS */}
  //           {/* ================================================= */}

  //           <Grid container spacing={3}>
  //             {Array.isArray(vTBDetails) && vTBDetails.length > 0 ? (
  //               vTBDetails.map((video: any) => (
  //                 <Grid item xs={12} sm={6} md={4} key={video?.article_id}>
  //                   <Card
  //                     sx={{
  //                       cursor: "pointer",

  //                       "&:hover": {
  //                         boxShadow: 6,
  //                       },

  //                       display: "flex",
  //                       flexDirection: "column",
  //                       height: "100%",
  //                     }}
  //                     onClick={() => handleCardClick(video)}
  //                   >
  //                     {/* CATEGORY */}
  //                     <Box
  //                       sx={{
  //                         bgcolor: "#0f4c81",
  //                         color: "white",
  //                         px: 2,
  //                         py: 0.5,
  //                         display: "flex",
  //                         justifyContent: "space-between",
  //                         alignItems: "center",
  //                       }}
  //                     >
  //                       <Typography
  //                         variant="caption"
  //                         sx={{
  //                           fontWeight: "bold",
  //                         }}
  //                       >
  //                         {video?.category}
  //                       </Typography>
  //                     </Box>

  //                     {/* IMAGE */}
  //                     <Box
  //                       sx={{
  //                         position: "relative",
  //                       }}
  //                     >
  //                       <CardMedia
  //                         component="img"
  //                         height="200"
  //                         image={imgThumbnail?.thumbnailUrl || ""}
  //                         alt="VTB video"
  //                       />

  //                       {/* PLAY ICON */}
  //                       <Box
  //                         sx={{
  //                           position: "absolute",
  //                           top: "50%",
  //                           left: "50%",
  //                           transform: "translate(-50%, -50%)",
  //                         }}
  //                       >
  //                         <PlayCircleOutlineIcon
  //                           sx={{
  //                             color: "white",
  //                             fontSize: 48,
  //                             opacity: 0.9,
  //                             filter: "drop-shadow(0px 0px 4px rgba(0,0,0,0.5))",
  //                           }}
  //                         />
  //                       </Box>
  //                     </Box>

  //                     {/* CARD CONTENT */}
  //                     <CardContent
  //                       sx={{
  //                         flexGrow: 1,
  //                         p: 2,
  //                       }}
  //                     >
  //                       <Typography
  //                         variant="subtitle2"
  //                         color="primary"
  //                         sx={{
  //                           fontWeight: "bold",
  //                           mb: 1,
  //                           lineHeight: 1.2,
  //                         }}
  //                       >
  //                         {video?.title}
  //                       </Typography>

  //                       <Typography
  //                         variant="caption"
  //                         color="text.secondary"
  //                         display="block"
  //                         sx={{
  //                           mb: 1,
  //                         }}
  //                       >
  //                         {video?.videoId}
  //                       </Typography>

  //                       <Typography variant="caption" color="text.secondary">
  //                         {video?.module}
  //                       </Typography>
  //                     </CardContent>

  //                     <Box
  //                       sx={{
  //                         display: "flex",
  //                         justifyContent: "space-between",
  //                         alignItems: "center",
  //                         px: 2,
  //                         pb: 1.5,
  //                         pt: 0,
  //                       }}
  //                     >
  //                       {/* Likes & Comments */}
  //                       <Box
  //                         sx={{ display: "flex", alignItems: "center", gap: 2 }}
  //                       >
  //                         <Box
  //                           sx={{
  //                             display: "flex",
  //                             alignItems: "center",
  //                             gap: 0.5,
  //                             color: "text.secondary",
  //                           }}
  //                         >
  //                           <ThumbUpOutlinedIcon sx={{ fontSize: 18 }} />
  //                           <Typography
  //                             variant="caption"
  //                             sx={{ fontWeight: 600 }}
  //                           >
  //                             {likeCounts[String(video?.article_id)] ?? 0}
  //                           </Typography>
  //                         </Box>
  //                         <Box
  //                           sx={{
  //                             display: "flex",
  //                             alignItems: "center",
  //                             gap: 0.5,
  //                             color: "text.secondary",
  //                           }}
  //                         >
  //                           <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
  //                           <Typography
  //                             variant="caption"
  //                             sx={{ fontWeight: 600 }}
  //                           >
  //                             {commentCounts[String(video?.article_id)] ?? 0}
  //                           </Typography>
  //                         </Box>

  //                         <Typography
  //                           variant="caption"
  //                           sx={{ fontWeight: 600, color: "text.secondary" }}
  //                         >
  //                           {`Stage-${video?.stage_of_cancer}`}
  //                         </Typography>
  //                       </Box>

  //                       {/* Bookmark Toggle */}
  //                       {/* <IconButton
  //                         size="small"
  //                         color="primary"
  //                         onClick={(e) => handleBookmarkToggle(e, video.id)}
  //                       >
  //                         {video.isSaved ? (
  //                           <BookmarkIcon />
  //                         ) : (
  //                           <BookmarkBorderIcon />
  //                         )}
  //                       </IconButton> */}
  //                     </Box>
  //                   </Card>
  //                 </Grid>
  //               ))
  //             ) : (
  //               <Grid item xs={12}>
  //                 <Box
  //                   sx={{
  //                     textAlign: "center",
  //                     py: 8,
  //                   }}
  //                 >
  //                   <Typography color="text.secondary">
  //                     No videos found.
  //                   </Typography>
  //                 </Box>
  //               </Grid>
  //             )}
  //           </Grid>

  //           {/* PAGINATION */}
  //           {count > 0 && (
  //             <Box
  //               my={3}
  //               sx={{
  //                 display: "flex",
  //                 justifyContent: "flex-end",
  //               }}
  //             >
  //               <Pagination
  //                 count={count}
  //                 handlePageChange={handlePageChange}
  //                 rowsPerResult={rows}
  //                 getRowsPerPage={getRowsPerPage}
  //                 currentPage={currentPage}
  //               />
  //             </Box>
  //           )}
  //         </Grid>

  //         {/* ================================================== */}
  //         {/* VIDEO DETAILS MODAL */}
  //         {/* ================================================== */}

  //         <Dialog
  //           open={isModalOpen}
  //           onClose={handleCloseModal}
  //           maxWidth="sm"
  //           fullWidth
  //         >
  //           {selectedVideo && (
  //             <>
  //               <DialogTitle
  //                 sx={{
  //                   display: "flex",
  //                   justifyContent: "space-between",
  //                   alignItems: "center",
  //                   pb: 1,
  //                 }}
  //               >
  //                 <Typography
  //                   variant="h6"
  //                   color="primary"
  //                   sx={{
  //                     fontWeight: "bold",
  //                     pr: 4,
  //                   }}
  //                 >
  //                   {selectedVideo.title}
  //                 </Typography>

  //                 <IconButton
  //                   onClick={handleCloseModal}
  //                   size="small"
  //                   sx={{
  //                     position: "absolute",
  //                     right: 8,
  //                     top: 8,
  //                   }}
  //                 >
  //                   <CloseIcon />
  //                 </IconButton>
  //               </DialogTitle>

  //               <DialogContent>
  //                 <img
  //                   src={imgThumbnail?.thumbnailUrl || ""}
  //                   alt="VTB Video"
  //                   style={{
  //                     width: "100%",
  //                     borderRadius: 8,
  //                     marginBottom: 16,
  //                   }}
  //                 />

  //                 <Typography
  //                   variant="body2"
  //                   color="text.secondary"
  //                   sx={{
  //                     mb: 3,
  //                   }}
  //                 >
  //                   {selectedVideo.title}
  //                 </Typography>

  //                 <Button
  //                   variant="contained"
  //                   startIcon={<PlayCircleOutlineIcon />}
  //                   sx={{
  //                     bgcolor: "#8870D7",
  //                     borderRadius: 2,
  //                     textTransform: "none",
  //                     px: 3,
  //                     width: "100%",
  //                   }}
  //                   onClick={handleWatchVideo}
  //                 >
  //                   Watch Video Now
  //                 </Button>
  //               </DialogContent>
  //             </>
  //           )}
  //         </Dialog>
  //       </Grid>
  //     );
  //   };

  const LibraryView = () => {
    // --------------------------------------------------------
    // INITIAL PAGE LOADING ONLY
    // --------------------------------------------------------

    if (initialLoading && (!vTBDetails || vTBDetails.length === 0)) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 10,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      );
    }

    return (
      <>
        {/* ================================================= */}
        {/* MAIN LIBRARY LAYOUT */}
        {/* ================================================= */}

        <Grid container spacing={4}>
          {/* =============================================== */}
          {/* DESKTOP FILTERS - LG AND ABOVE */}
          {/* =============================================== */}

          <Grid
            item
            xs={12}
            lg={3}
            sx={{
              display: {
                xs: "none",
                lg: "block",
              },
            }}
          >
            {/* RESULTS COUNT */}
            {/* <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                mb: 3,
              }}
            >
              Results {totalHits}
            </Typography> */}

            {/* FILTERS */}
            <VTBFilters />
          </Grid>

          {/* =============================================== */}
          {/* MAIN CONTENT */}
          {/* =============================================== */}

          <Grid item xs={12} lg={9}>
            {/* ============================================= */}
            {/* SEARCH + DESKTOP SORT */}
            {/* ============================================= */}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: {
                  xs: 2,
                  lg: 3,
                },

                flexDirection: {
                  xs: "column",
                  lg: "row",
                },

                width: "100%",
              }}
            >
              {/* ========================================= */}
              {/* SEARCH */}
              {/* ========================================= */}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "stretch",

                  width: {
                    xs: "100%",
                    lg: "auto",
                  },

                  flex: {
                    xs: "none",
                    lg: 1,
                  },

                  minWidth: 0,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by title"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  InputProps={{
                    endAdornment: searchQuery ? (
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        edge="end"
                        aria-label="clear search"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ) : null,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: 40,
                      backgroundColor: "#fff",

                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,

                      "& fieldset": {
                        borderColor: "#d0d5dd",
                      },

                      "&:hover fieldset": {
                        borderColor: "#0f4c81",
                      },

                      "&.Mui-focused fieldset": {
                        borderColor: "#0f4c81",
                      },
                    },

                    "& .MuiInputBase-input": {
                      fontSize: "14px",
                    },
                  }}
                />

                {/* SEARCH BUTTON */}

                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={searchLoading}
                  sx={{
                    height: 40,

                    minWidth: {
                      xs: 90,
                      sm: 100,
                      lg: 100,
                    },

                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,

                    textTransform: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",

                    bgcolor: "#0f4c81",

                    "&:hover": {
                      bgcolor: "#08365c",
                    },

                    "&.Mui-disabled": {
                      bgcolor: "#0f4c81",
                      color: "#fff",
                      opacity: 0.8,
                    },
                  }}
                >
                  {searchLoading ? (
                    <>
                      <CircularProgress
                        size={18}
                        color="inherit"
                        sx={{
                          mr: {
                            xs: 0,
                            sm: 1,
                          },
                        }}
                      />

                      <Box
                        component="span"
                        sx={{
                          display: {
                            xs: "none",
                            sm: "inline",
                          },
                        }}
                      >
                        Searching...
                      </Box>
                    </>
                  ) : (
                    "Search"
                  )}
                </Button>
              </Box>

              {/* ========================================= */}
              {/* DESKTOP SORT - LG AND ABOVE */}
              {/* ========================================= */}

              <TextField
                select
                size="small"
                value={sortOrder}
                disabled
                onChange={(event) => setSortOrder(event.target.value)}
                sx={{
                  display: {
                    xs: "none",
                    lg: "flex",
                  },

                  width: 250,
                  flexShrink: 0,

                  "& .MuiOutlinedInput-root": {
                    height: 40,
                    backgroundColor: "#fff",
                  },

                  "& .MuiInputBase-input": {
                    fontSize: "14px",
                  },
                }}
              >
                <MenuItem value="sort">SORT</MenuItem>

                <MenuItem value="latest">
                  Presenting Year (Latest First)
                </MenuItem>

                <MenuItem value="earliest">
                  Presenting Year (Earliest First)
                </MenuItem>
              </TextField>
            </Box>

            {/* ============================================= */}
            {/* MOBILE / TABLET RESULTS + FILTER + SORT */}
            {/* XS / SM / MD ONLY */}
            {/* ============================================= */}

            <Box
              sx={{
                display: {
                  xs: "flex",
                  lg: "none",
                },

                alignItems: "center",
                justifyContent: "space-between",

                mb: 3,
              }}
            >
              {/* RESULTS */}

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,

                  fontSize: {
                    xs: "20px",
                    sm: "24px",
                  },
                }}
              >
                Results {totalHits}
              </Typography>

              {/* FILTER + SORT ICONS */}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {/* FILTER ICON */}

                <IconButton
                  onClick={handleOpenFilters}
                  aria-label="open filters"
                  sx={{
                    border: "1px solid #d0d5dd",
                    borderRadius: 1,

                    color: "#0f4c81",

                    width: 40,
                    height: 40,

                    "&:hover": {
                      backgroundColor: "#f2f6fa",
                    },
                  }}
                >
                  {/* <FilterListIcon /> */}
                  <FilterAlt />
                </IconButton>

                {/* SORT ICON */}

                <IconButton
                  disabled
                  onClick={handleOpenSort}
                  aria-label="open sort"
                  sx={{
                    border: "1px solid #d0d5dd",
                    borderRadius: 1,

                    color: "#0f4c81",

                    width: 40,
                    height: 40,

                    "&:hover": {
                      backgroundColor: "#f2f6fa",
                    },
                  }}
                >
                  <SortIcon />
                </IconButton>
              </Box>
            </Box>

            {/* ================================================= */}
            {/* VIDEO CARDS */}
            {/* ================================================= */}

            <Grid container spacing={3}>
              {Array.isArray(vTBDetails) && vTBDetails.length > 0 ? (
                vTBDetails.map((video: any) => (
                  <Grid item xs={12} sm={6} md={4} key={video?.article_id}>
                    <Card
                      sx={{
                        cursor: "pointer",

                        "&:hover": {
                          boxShadow: 6,
                        },

                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                      }}
                      onClick={() => handleCardClick(video)}
                    >
                      {/* CATEGORY */}

                      <Box
                        sx={{
                          bgcolor: "#0f4c81",
                          color: "white",
                          px: 2,
                          py: 0.5,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: "bold",
                          }}
                        >
                          {video?.category}
                        </Typography>
                      </Box>

                      {/* IMAGE */}

                      <Box
                        sx={{
                          position: "relative",
                        }}
                      >
                        <CardMedia
                          component="img"
                          height="200"
                          // image={imgThumbnail?.thumbnailUrl || ""}
                          image={`https://jgatenext.com/cdn/ncgvideotn/thumbnails/${video?.video_id}.jpg`}
                          alt="VTB video"
                        />

                        {/* PLAY ICON */}

                        <Box
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                          }}
                        >
                          <PlayCircleOutlineIcon
                            sx={{
                              color: "white",
                              fontSize: 48,
                              opacity: 0.9,
                              filter:
                                "drop-shadow(0px 0px 4px rgba(0,0,0,0.5))",
                            }}
                          />
                        </Box>
                      </Box>

                      {/* CARD CONTENT */}

                      <CardContent
                        sx={{
                          flexGrow: 1,
                          p: 2,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          sx={{
                            fontWeight: "bold",
                            mb: 1,
                            lineHeight: 1.2,
                          }}
                        >
                          {video?.title}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{
                            mb: 1,
                          }}
                        >
                          {video?.videoId}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                          {video?.module}
                        </Typography>
                      </CardContent>

                      {/* LIKES + COMMENTS */}

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          px: 2,
                          pb: 1.5,
                          pt: 0,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          {/* LIKES */}

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: "text.secondary",
                            }}
                          >
                            <ThumbUpOutlinedIcon
                              sx={{
                                fontSize: 18,
                              }}
                            />

                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                              }}
                            >
                              {likeCounts[String(video?.article_id)] ?? 0}
                            </Typography>
                          </Box>

                          {/* COMMENTS */}

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: "text.secondary",
                            }}
                          >
                            <ChatBubbleOutlineIcon
                              sx={{
                                fontSize: 18,
                              }}
                            />

                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                              }}
                            >
                              {commentCounts[String(video?.article_id)] ?? 0}
                            </Typography>
                          </Box>

                          {/* STAGE */}

                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: "text.secondary",
                            }}
                          >
                            {`Stage-${video?.stage_of_cancer}`}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 8,
                    }}
                  >
                    <Typography color="text.secondary">
                      No videos found.
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>

            {/* ================================================= */}
            {/* PAGINATION */}
            {/* ================================================= */}

            {count > 0 && (
              <Box
                my={3}
                sx={{
                  display: "flex",
                  justifyContent: {
                    xs: "center",
                    sm: "flex-end",
                  },
                }}
              >
                <Pagination
                  count={count}
                  handlePageChange={handlePageChange}
                  rowsPerResult={rows}
                  getRowsPerPage={getRowsPerPage}
                  currentPage={currentPage}
                />
              </Box>
            )}
          </Grid>
        </Grid>

        {/* ================================================== */}
        {/* MOBILE / TABLET FILTER DRAWER */}
        {/* XS / SM / MD ONLY */}
        {/* ================================================== */}

        <Drawer
          anchor="left"
          open={isFilterDrawerOpen}
          onClose={handleCloseFilters}
          PaperProps={{
            sx: {
              width: {
                xs: "85%",
                sm: 400,
              },
              maxWidth: "100%",
            },
          }}
        >
          <Box
            sx={{
              p: 2,
              height: "100%",
              overflowY: "auto",
            }}
          >
            {/* DRAWER HEADER */}

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                }}
              >
                Filters
              </Typography>

              <IconButton
                onClick={handleCloseFilters}
                aria-label="close filters"
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* EXISTING FILTER COMPONENT */}

            <VTBFilters />
          </Box>
        </Drawer>

        {/* ================================================== */}
        {/* MOBILE / TABLET SORT DIALOG */}
        {/* XS / SM / MD ONLY */}
        {/* ================================================== */}

        <Dialog
          open={isSortDialogOpen}
          onClose={handleCloseSort}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
              }}
            >
              Sort
            </Typography>

            <IconButton
              onClick={handleCloseSort}
              aria-label="close sort"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <TextField
              select
              fullWidth
              size="small"
              value={sortOrder}
              disabled
              onChange={(event) => setSortOrder(event.target.value)}
              sx={{
                mt: 1,

                "& .MuiOutlinedInput-root": {
                  height: 40,
                  backgroundColor: "#fff",
                },

                "& .MuiInputBase-input": {
                  fontSize: "14px",
                },
              }}
            >
              <MenuItem value="sort">SORT</MenuItem>

              <MenuItem value="latest">Presenting Year (Latest First)</MenuItem>

              <MenuItem value="earliest">
                Presenting Year (Earliest First)
              </MenuItem>
            </TextField>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              pb: 2,
            }}
          >
            <Button
              onClick={handleCloseSort}
              sx={{
                textTransform: "none",
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* ================================================== */}
        {/* VIDEO DETAILS MODAL */}
        {/* ================================================== */}

        <Dialog
          open={isModalOpen}
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
        >
          {selectedVideo && (
            <>
              <DialogTitle
                sx={{
                  display: "flex",
                  //   justifyContent: "space-between",
                  alignItems: "center",
                  pb: 4,
                }}
              >
                {/* <Typography
                  variant="h6"
                  color="primary"
                  sx={{
                    fontWeight: "bold",
                    pr: 4,
                  }}
                >
                  {selectedVideo.title}
                </Typography> */}

                <IconButton
                  onClick={handleCloseModal}
                  size="small"
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent>
                <img
                  // src={imgThumbnail?.thumbnailUrl || ""}
                  src={`https://jgatenext.com/cdn/ncgvideotn/thumbnails/${selectedVideo?.video_id}.jpg`}
                  alt="VTB Video"
                  style={{
                    width: "100%",
                    borderRadius: 8,
                    marginBottom: 16,
                  }}
                />

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 3,
                  }}
                >
                  {selectedVideo.title}
                </Typography>

                <Button
                  variant="contained"
                  startIcon={<PlayCircleOutlineIcon />}
                  sx={{
                    bgcolor: "#8870D7",
                    borderRadius: 2,
                    textTransform: "none",
                    px: 3,
                    width: "100%",
                  }}
                  onClick={handleWatchVideo}
                >
                  Watch Video Now
                </Button>
              </DialogContent>
            </>
          )}
        </Dialog>
      </>
    );
  };

  // ==========================================================
  // DETAIL VIEW
  // ==========================================================

  const DetailView = () => {
    if (!selectedVideo) {
      return null;
    }
    return (
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <VTBVideoPlayer
            selectedVideo={selectedVideo}
            apiBase={VTB_API_BASE}
            apiKey={VTB_API_KEY}
            hlsVideoId={HLS_VIDEO_ID}
            // hlsVideoId={selectedVideo.videoId || ""}
            onBack={() => setCurrentView(0)}
            onCommentClick={handleCommentClick}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <VTBUpNext
            videos={vTBDetails || []}
            selectedVideo={selectedVideo}
            onVideoSelect={handleSidebarClick}
            // thumbnailUrl={imgThumbnail?.thumbnailUrl || ""}
            // thumbnailUrl={`https://jgatenext.com/cdn/ncgvideotn/thumbnails/${vTBDetails?.video_id}.jpg`}
          />

          <Box mt={3} ref={commentsSectionRef}>
            <VTBCommentsSection
              articleId={selectedVideo?.article_id}
              focusCommentInput={focusCommentInput}
            />
          </Box>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box
      sx={{
        bgcolor: "#f5f5f5",
        minHeight: "100vh",
        pb: 5,
      }}
    >
      <Box
        sx={{
          bgcolor: "white",
          borderBottom: "1px solid #ddd",
          mb: 3,
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              py: 2,
              gap: 2,
            }}
          >
            {NCG_logo && (
              <img
                src={NCG_logo}
                alt="NCG logo"
                style={{
                  height: "40px",
                }}
              />
            )}

            <Typography
              variant="h6"
              color="primary"
              sx={{
                fontWeight: "bold",
                letterSpacing: 1,
              }}
            >
              NCG VIRTUAL TUMOUR BOARD VIDEO LIBRARY
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl">
        {/* ================================================== */}
        {/* LIBRARY WELCOME TEXT */}
        {/* ================================================== */}

        {currentView === 0 && (
          <Box>
            <Typography
              sx={{
                mb: 2,
                color: "text.secondary",
              }}
            >
              Welcome to the NCG Virtual Tumor Board Video Library, your
              comprehensive resource for oncology professionals to enhance
              research and collaboration in tumor diagnosis and treatment.
            </Typography>
          </Box>
        )}

        {/* ================================================== */}
        {/* LIBRARY */}
        {/* ================================================== */}

        {/* {currentView === 0 && <LibraryView />}
        {currentView === 1 && <DetailView />} */}

        {currentView === 0 && LibraryView()}
        {currentView === 1 && DetailView()}
      </Container>
    </Box>
  );
}
